import React, { useEffect, useRef, useState } from "react";
import WorkScreen from "./WorkScreen";
import DemoModeControl from "./DemoModeControl";
import { PRODUCT_MISSIONS } from "../data/product-demo";
import Check from "@mui/icons-material/Check";
import NearMe from "@mui/icons-material/NearMe";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Replay from "@mui/icons-material/Replay";
import DesktopMac from "@mui/icons-material/DesktopMac";
import PhoneIphone from "@mui/icons-material/PhoneIphone";
import "../styles/mission-preview.css";

export const MISSION_STEP_DURATION = 5000;

function Journey({ mission, stage, completed }) {
  return (
    <div
      className="mission-journey"
      aria-label={
        "Mission journey: " +
        (completed ? "Preview complete" : mission.steps[stage])
      }
    >
      <div className="mission-journey-label">
        <span>This work cycle</span>
        <span>
          {mission.day} <span className="mission-divider">/</span> Est.{" "}
          {mission.eta} remaining
        </span>
      </div>
      <div
        className="mission-route"
        style={{ "--mission-step-count": mission.steps.length }}
      >
        <div className="mission-route-base" />
        <div
          className="mission-route-done"
          style={{ width: (stage / mission.steps.length) * 100 + "%" }}
        />
        {mission.steps.map((step, i) => (
          <div
            className={
              "mission-waypoint " +
              (completed || i < stage
                ? "complete"
                : i === stage
                  ? "current"
                  : "")
            }
            key={step}
          >
            <span>
              {completed || i < stage ? (
                <Check />
              ) : i === stage ? (
                <NearMe />
              ) : (
                <span />
              )}
            </span>
            <span>{step}</span>
          </div>
        ))}
      </div>
      <div className="mission-endpoints">
        <span>{mission.start}</span>
        <span>{mission.destination}</span>
      </div>
    </div>
  );
}

export default function MissionPreview() {
  const showcase = useRef(null);
  const [selected, setSelected] = useState(0);
  const [stages, setStages] = useState(() =>
    PRODUCT_MISSIONS.map((mission) => mission.initialStage),
  );
  const [playback, setPlayback] = useState("auto");
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => typeof document === "undefined" || !document.hidden);
  const [reducedMotion, setReducedMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false),
  );
  const [view, setView] = useState("desktop");
  const [mode, setMode] = useState("autonomous");
  const mission = PRODUCT_MISSIONS[selected];
  const position = stages[selected];
  const completed = position === mission.steps.length;
  const stage = Math.min(position, mission.steps.length - 1);
  const playing =
    inView &&
    pageVisible &&
    !completed &&
    (playback === "playing" || (playback === "auto" && !reducedMotion));
  useEffect(() => {
    const screen = showcase.current?.querySelector(".work-screen");
    if (!screen) return undefined;
    if (!window.IntersectionObserver) {
      setInView(true);
      setPlayback("paused");
      return undefined;
    }
    setInView(false);
    const observer = new IntersectionObserver(
      ([entry]) =>
        setInView(entry.isIntersecting && entry.intersectionRatio >= 0.25),
      { threshold: 0.25, rootMargin: "-80px 0px 0px" },
    );
    observer.observe(screen);
    return () => observer.disconnect();
  }, [selected]);
  useEffect(() => {
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const onVisibility = () => setPageVisible(!document.hidden);
    const onMotion = () => setReducedMotion(media.matches);
    document.addEventListener("visibilitychange", onVisibility);
    if (media?.addEventListener) media.addEventListener("change", onMotion);
    else media?.addListener?.(onMotion);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (media?.removeEventListener)
        media.removeEventListener("change", onMotion);
      else media?.removeListener?.(onMotion);
    };
  }, []);
  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setTimeout(() => {
      setStages((prev) =>
        prev.map((s, i) =>
          i === selected ? Math.min(s + 1, mission.steps.length) : s,
        ),
      );
      if (mode === "supervised") setPlayback("paused");
    }, MISSION_STEP_DURATION);
    return () => window.clearTimeout(timer);
  }, [playing, selected, position, mode, mission.steps.length]);
  const selectMission = (index) => {
    setSelected(index);
  };
  const replay = () => {
    setStages((prev) => prev.map((s, i) => (i === selected ? 0 : s)));
    setPlayback("playing");
  };
  const selectStage = (nextStage) => {
    setStages((prev) => prev.map((s, i) => (i === selected ? nextStage : s)));
    setPlayback("paused");
  };
  const status = completed
    ? "Work cycle shown. Mission continues."
    : playing
      ? mission.activity[stage]
      : `${mission.steps[stage]} / Paused`;
  return (
    <div className="mission-showcase" ref={showcase}>
      <div className="brand-preview-toolbar">
        <span className="brand-caption">Momentum / Product preview</span>
        <div
          className="brand-segments"
          role="group"
          aria-label="Product preview device"
        >
          <button
            aria-pressed={view === "desktop"}
            onClick={() => setView("desktop")}
          >
            <DesktopMac fontSize="small" /> Desktop
          </button>
          <button
            aria-pressed={view === "mobile"}
            onClick={() => setView("mobile")}
          >
            <PhoneIphone fontSize="small" /> Mobile
          </button>
        </div>
      </div>
      <div className={"mission-stage mission-stage-" + view}>
        <div
          className={
            "mission-app " + (view === "mobile" ? "mission-app-phone" : "")
          }
        >
          <div className="mission-app-top">
            <div>
              <strong>Momentum</strong>
              <span className="mission-byline">by GrupaAI</span>
            </div>
            <DemoModeControl value={mode} onChange={setMode} label="Momentum" />
          </div>
          <div className="mission-app-body">
            <aside className="mission-sidebar">
              <div className="mission-sidebar-heading">
                Your missions{" "}
                <span>{String(PRODUCT_MISSIONS.length).padStart(2, "0")}</span>
              </div>
              {PRODUCT_MISSIONS.map((item, i) => (
                <button
                  key={item.id}
                  aria-pressed={selected === i}
                  onClick={() => selectMission(i)}
                >
                  <NearMe fontSize="small" />
                  <span>
                    <strong>{item.name}</strong>
                    <span>{item.short}</span>
                    <span className="mission-sidebar-track">
                      <i
                        style={{
                          width:
                            (Math.min(stages[i] + 1, item.steps.length) /
                              item.steps.length) *
                              100 +
                            "%",
                        }}
                      />
                    </span>
                  </span>
                </button>
              ))}
              <div className="mission-sidebar-bottom">
                <span className="brand-status-dot" />
                {PRODUCT_MISSIONS.length} missions, moving together
              </div>
            </aside>
            <div className="mission-main">
              <div className="mission-mobile-switch">
                <label htmlFor="preview-mission">Mission</label>
                <select
                  id="preview-mission"
                  value={selected}
                  onChange={(e) => selectMission(Number(e.target.value))}
                >
                  {PRODUCT_MISSIONS.map((item, i) => (
                    <option key={item.id} value={i}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mission-destination">
                <div>
                  <span className="mission-small-label">Your destination</span>
                  <h3>{mission.name}</h3>
                </div>
                <div className="mission-outcome">
                  <strong>{mission.count}</strong>
                  <span>{mission.measure}</span>
                </div>
              </div>
              <Journey mission={mission} stage={stage} completed={completed} />
              <WorkScreen
                key={mission.id}
                mission={mission}
                stage={stage}
                mode={mode}
                onInteraction={() => setPlayback("paused")}
                onStageChange={selectStage}
              />
              <div className="mission-player">
                <div role="status">
                  <span className="brand-status-dot" />
                  <span>{status}</span>
                </div>
                <button
                  onClick={
                    completed
                      ? replay
                      : () => setPlayback(playing ? "paused" : "playing")
                  }
                  aria-label={
                    completed
                      ? "Replay mission demonstration"
                      : playing
                        ? "Pause mission demonstration"
                        : "Play mission demonstration"
                  }
                  title={
                    completed
                      ? "Replay demonstration"
                      : playing
                        ? "Pause demonstration"
                        : "Play demonstration"
                  }
                >
                  {completed ? <Replay /> : playing ? <Pause /> : <PlayArrow />}
                </button>
              </div>
              <p className="mission-mode-note">
                {mode === "autonomous"
                  ? "Working within your permissions. Exceptions come to you."
                  : "Milestones pause for review before the mission continues."}
              </p>
            </div>
            <aside className="mission-detail">
              <span className="mission-small-label">Along the way</span>
              <h4>
                Work you can
                <br />
                follow.
              </h4>
              {mission.activity.map((event, i) => (
                <div
                  className={
                    "mission-event " + (i <= stage ? "is-reached" : "")
                  }
                  key={event}
                >
                  {completed || i < stage ? <Check /> : <NearMe />}
                  <div>
                    <strong>{event}</strong>
                    <span>
                      {completed || i < stage
                        ? "Complete"
                        : i === stage
                          ? "In progress"
                          : "Up next"}
                    </span>
                  </div>
                </div>
              ))}
              <div className="mission-detail-note">
                <span className="mission-small-label">The outcome</span>
                <p>{mission.destination}</p>
              </div>
            </aside>
          </div>
        </div>
      </div>
      <p className="mission-disclosure">
        Illustrative app views and mission data. This product preview does not
        run live work or send messages.
      </p>
    </div>
  );
}
